'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import type { NormalizedResource } from '@/lib/cms-resources'
import { SERVICE_CATEGORY_TABS } from '@/lib/service-category-tabs'
import { cardLogoFallbackClass, featuredOrToolImage } from '@/lib/service-tool-logos'
import WorksWithLogos from '@/components/services/WorksWithLogos'

interface ServicesTabsSectionProps {
  posts: NormalizedResource[]
}

const CATEGORY_TABS = SERVICE_CATEGORY_TABS.map((tab) => ({
  ...tab,
  alwaysShow: tab.slug === 'smart-audits',
}))

function ServiceLogo({ post }: { post: NormalizedResource }) {
  const { src, fallbackLabel } = featuredOrToolImage(post)
  const [imgError, setImgError] = useState(false)

  if (src && !imgError) {
    return (
      <img
        src={src}
        alt=""
        width={42}
        height={42}
        onError={() => setImgError(true)}
        className="h-[42px] w-[42px] shrink-0 rounded-xl border border-slate-100 bg-white object-contain p-1.5 shadow-sm"
      />
    )
  }

  return (
    <div
      className={`flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl border border-slate-100 text-sm font-bold shadow-sm ${cardLogoFallbackClass(fallbackLabel)}`}
      aria-hidden
    >
      {fallbackLabel.trim().charAt(0).toUpperCase() || '?'}
    </div>
  )
}

function CardHeader({ post }: { post: NormalizedResource }) {
  const hasWorksWith = Array.isArray(post.worksWithTools) && post.worksWithTools.length > 0

  if (hasWorksWith) {
    return (
      <div className="mb-3">
        <h3 className="mb-2 text-[15px] font-bold leading-snug text-slate-900 transition-colors group-hover:text-blue-700">
          {post.title}
        </h3>
        <WorksWithLogos toolIds={post.worksWithTools} size="lg" />
      </div>
    )
  }

  return (
    <div className="mb-3 flex items-center gap-3">
      <ServiceLogo post={post} />
      <h3 className="min-w-0 flex-1 text-[15px] font-bold leading-snug text-slate-900 transition-colors group-hover:text-blue-700">
        {post.title}
      </h3>
    </div>
  )
}

export function ServiceCard({ post }: { post: NormalizedResource }) {
  return (
    <Link
      href={`/services/${post.slug}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-[18px] shadow-sm transition-all duration-200 hover:border-blue-200 hover:shadow-md"
    >
      <CardHeader post={post} />

      {post.excerpt && (
        <p className="mb-3 line-clamp-4 text-sm leading-relaxed text-slate-500">{post.excerpt}</p>
      )}

      <div className="mt-auto border-t border-slate-100 pt-3">
        <span className="flex items-center justify-center gap-1 rounded-full bg-[#13479c] py-2 text-[12px] font-bold text-white transition-colors group-hover:bg-[#0f3a7a]">
          Get Started
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 8h10M9 4l4 4-4 4" />
          </svg>
        </span>
      </div>
    </Link>
  )
}

export default function ServicesTabsSection({ posts }: ServicesTabsSectionProps) {
  const availableTabs = CATEGORY_TABS.filter(
    (tab) => tab.alwaysShow || posts.some((p) => p.serviceCategory?.includes(tab.slug))
  )

  const [activeTab, setActiveTab] = useState<string>(availableTabs[0]?.slug ?? '')
  const hasSmartAuditsTab = availableTabs.some((t) => t.slug === 'smart-audits')

  useEffect(() => {
    if (!hasSmartAuditsTab) return

    const selectSmartAudits = () => setActiveTab('smart-audits')

    const applyHash = () => {
      if (window.location.hash === '#smart-audits') selectSmartAudits()
    }

    applyHash()
    window.addEventListener('hashchange', applyHash)
    window.addEventListener('select-service-tab', selectSmartAudits)
    return () => {
      window.removeEventListener('hashchange', applyHash)
      window.removeEventListener('select-service-tab', selectSmartAudits)
    }
  }, [hasSmartAuditsTab])

  return (
    <div id="smart-audits">
      <noscript>
        <style>{`.home-svc-panel { display: grid !important; }`}</style>
      </noscript>
      {/* Category tabs */}
      <div className="mb-8 border-b border-slate-200 overflow-x-auto">
        <div className="flex min-w-max" role="tablist" aria-label="Service categories">
          {availableTabs.map((tab) => {
            const { Icon, iconColor } = tab
            const isSelected = activeTab === tab.slug
            return (
              <button
                key={tab.slug}
                type="button"
                role="tab"
                id={`home-svc-tab-${tab.slug}`}
                aria-selected={isSelected}
                aria-controls={`home-svc-panel-${tab.slug}`}
                onClick={() => setActiveTab(tab.slug)}
                className={`flex items-center gap-2 px-5 py-3 text-base font-bold transition-all duration-150 whitespace-nowrap border-b-2 ${
                  isSelected
                    ? 'border-[#0478FB] text-[#0478FB]'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${isSelected ? 'text-[#0478FB]' : iconColor}`} />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {availableTabs.map((tab) => {
        const cards = posts
          .filter((p) => p.serviceCategory?.includes(tab.slug))
          .sort((a, b) => String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' }))
        const isSelected = activeTab === tab.slug
        return (
          <div
            key={tab.slug}
            id={`home-svc-panel-${tab.slug}`}
            role="tabpanel"
            aria-labelledby={`home-svc-tab-${tab.slug}`}
            className="home-svc-panel grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            style={isSelected ? undefined : { display: 'none' }}
          >
            {cards.length === 0 ? (
              <div className="col-span-full text-center py-16 text-slate-400">
                <p className="text-lg font-medium">No services found.</p>
              </div>
            ) : (
              cards.map((post) => <ServiceCard key={post.id} post={post} />)
            )}
          </div>
        )
      })}
    </div>
  )
}
